import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth/auth-context';
import { AuthError } from '@/lib/auth/session';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/logo';
import { cn } from '@/lib/cn';

const field =
  'w-full rounded-md border border-border-default bg-bg px-3 py-2 text-sm text-fg ' +
  'placeholder:text-fg-subtle focus-visible:outline-2 focus-visible:outline-offset-0 ' +
  'focus-visible:outline-ring';

/**
 * Sign-in, including the forced-password-change challenge.
 *
 * Both halves are here rather than on separate routes, as a state machine in
 * one component: while Cognito is waiting for a new password, that is the
 * only thing on screen, so the challenge cannot be navigated away from
 * mid-flow.
 */
export const SignInScreen = () => {
  const { t } = useTranslation();
  const { status, signIn, completeNewPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const needsNewPassword = status === 'new-password-required';

  /**
   * Turns a Cognito failure into something to show, per step.
   *
   * **Why the step matters.** `InvalidPasswordException` means "does not meet
   * the policy" when *setting* a password — but `cognito-local` also returns it
   * for a plain wrong password at sign-in, where real Cognito returns
   * `NotAuthorizedException`. Mapping codes to messages without knowing the step
   * therefore tells a local developer their password is malformed when it is
   * merely wrong, and tells a production user nothing useful either.
   *
   * At sign-in the message is deliberately the same whatever the reason:
   * distinguishing "no such account" from "wrong password" tells an attacker
   * which email addresses exist.
   */
  const messageFor = (caught: unknown, step: 'sign-in' | 'set-password'): string => {
    const code = caught instanceof AuthError ? caught.code : 'unknown';

    if (code === 'TooManyRequestsException' || code === 'LimitExceededException') {
      return t('auth.errors.tooManyRequests');
    }

    if (code === 'NetworkError') {
      return t('auth.errors.network');
    }

    if (step === 'set-password') {
      return code === 'InvalidPasswordException'
        ? t('auth.errors.passwordPolicy')
        : t('auth.errors.unknown');
    }

    const credentialFailures = new Set([
      'NotAuthorizedException',
      'UserNotFoundException',
      'InvalidPasswordException',
      'InvalidParameterException',
    ]);

    return credentialFailures.has(code) ? t('auth.errors.credentials') : t('auth.errors.unknown');
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);

    if (needsNewPassword && newPassword !== confirmation) {
      setError(t('auth.passwordsDoNotMatch'));

      return;
    }

    setBusy(true);
    try {
      if (needsNewPassword) {
        await completeNewPassword(newPassword);
      } else {
        await signIn(email.trim(), password);
      }
    } catch (caught) {
      setError(messageFor(caught, needsNewPassword ? 'set-password' : 'sign-in'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="w-full max-w-sm">
        {/*
          The stacked lockup, which is the arrangement the brand design draws for
          a login screen: mark above the wordmark, centred. The header uses the
          horizontal one — same mark, and it is the only place either appears.
        */}
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <Logo className="size-12 text-brand" />
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-fg">{t('app.name')}</h1>
            <p className="text-sm text-fg-muted">{t('app.tagline')}</p>
          </div>
        </div>

        <form
          onSubmit={(event) => void onSubmit(event)}
          className="space-y-4 rounded-lg border border-border-default bg-bg-raised p-5"
          noValidate
        >
          <div>
            <h2 className="text-sm font-semibold text-fg">
              {needsNewPassword ? t('auth.newPasswordRequired') : t('auth.signInTitle')}
            </h2>
            <p className="mt-0.5 text-xs text-fg-muted">
              {needsNewPassword ? t('auth.newPasswordRequiredHint') : t('auth.signInSubtitle')}
            </p>
          </div>

          {needsNewPassword ? (
            <>
              <label className="block space-y-1">
                <span className="text-xs font-medium text-fg-muted">{t('auth.newPassword')}</span>
                <input
                  className={field}
                  type="password"
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                />
              </label>
              <label className="block space-y-1">
                <span className="text-xs font-medium text-fg-muted">
                  {t('auth.confirmNewPassword')}
                </span>
                <input
                  className={field}
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                />
              </label>
            </>
          ) : (
            <>
              <label className="block space-y-1">
                <span className="text-xs font-medium text-fg-muted">{t('auth.email')}</span>
                <input
                  className={field}
                  type="email"
                  autoComplete="username"
                  required
                  autoFocus
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
              <label className="block space-y-1">
                <span className="text-xs font-medium text-fg-muted">{t('auth.password')}</span>
                <input
                  className={field}
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </label>
            </>
          )}

          {error ? (
            // role=alert so a screen reader announces a failed sign-in; without
            // it the only feedback is visual and the form looks inert.
            <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-xs text-danger-fg">
              {error}
            </p>
          ) : null}

          <Button type="submit" disabled={busy} className={cn('w-full')}>
            {busy
              ? t('auth.signingIn')
              : needsNewPassword
                ? t('auth.setPassword')
                : t('auth.signIn')}
          </Button>
        </form>
      </div>
    </div>
  );
};
